import { SearchParams } from "@/entry/local/movie";
import { SearchResult, SearchResultAgent } from "@/entry/server/movie";
import { Pagination } from "@/lib/api";
import React, { useEffect, useState } from "react";
import LoadingGif from "@public/img/loading.gif";
import SectionTopBg from "@public/img/section-top-bg.png";
import PaginationView from "./pagination";
import MovieCard from "./movie-card";
import { ASSETS_BASE_URL } from "@/utils/constants";
import NoPersonIcon from "@public/img/no-person.png";
import { Link } from "react-router-dom";
import CommonFilter from "../common-filter";
import { videoRepo } from "@/repo/video-repo";
import "@/styles/movies-grid.css";

type MoviesGridProps = {
    defaultSearchResult: SearchResult;
    defaultPagination: Pagination;
    title: string;
    defaultSearchParams: SearchParams;
    allowType?: boolean;
    allowGenre?: boolean;
    allowSort?: boolean;
    allowImdbRating?: boolean;
    allowDubbed?: boolean;
    allowSubtitled?: boolean;
}

export default function MoviesGrid({
    defaultSearchResult,
    defaultPagination,
    title,
    defaultSearchParams,
    allowType = true,
    allowGenre = true,
    allowSort = true,
    allowImdbRating = true,
    allowDubbed = true,
    allowSubtitled = true,
}: MoviesGridProps) {
    const [searchResult, setSearchResult] = useState<SearchResult>(defaultSearchResult);
    const [searchParams, setSearchParams] = useState<SearchParams>(defaultSearchParams);
    const [pagination, setPagination] = useState<Pagination>(defaultPagination);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(function () {
        updateQueryParams();
        fetchMovies();
    }, [searchParams]);

    const fetchMovies = async function () {
        setIsLoading(true);
        const response = await videoRepo.search(searchParams);
        if (response.status && response.data) {
            setSearchResult(response.data);
            if (response.pagination) {
                setPagination(response.pagination);
            }
        }
        setIsLoading(false);
    };

    const onPageChange = async function (page: number) {
        setSearchParams(Object.assign({}, searchParams, { page: page }));
    };

    const onFiltersChange = function (type: keyof SearchParams, value: string | boolean) {
        var nextParams = Object.assign({}, searchParams);
        nextParams[type] = value as never;
        setSearchParams(nextParams);
    };

    const updateQueryParams = function () {
        var isSearchPage = window.location.pathname === "/search";
        var queryParams: Record<string, string> = {};

        if (searchParams.s) {
            queryParams.s = searchParams.s;
        }
        if (searchParams.type) {
            queryParams.type = searchParams.type;
        }
        if (searchParams.genre) {
            queryParams.genre = searchParams.genre.toString();
        }
        if (searchParams.sort) {
            queryParams.sort = searchParams.sort;
        }
        if (searchParams.imdb_rate) {
            queryParams.imdb_rate = searchParams.imdb_rate.toString();
        }
        if (searchParams.is_dubbed) {
            queryParams.is_dubbed = searchParams.is_dubbed.toString();
        }
        if (searchParams.is_subbed) {
            queryParams.is_subbed = searchParams.is_subbed.toString();
        }
        if (searchParams.limit) {
            queryParams.limit = searchParams.limit.toString();
        }
        if (searchParams.page) {
            queryParams.page = searchParams.page.toString();
        }

        if (searchParams.start_year && isSearchPage) {
            queryParams.start_year = searchParams.start_year.toString();
        }
        if (searchParams.end_year && isSearchPage) {
            queryParams.end_year = searchParams.end_year.toString();
        }
        if (searchParams.status && isSearchPage) {
            queryParams.status = searchParams.status;
        }
        if (searchParams.country && isSearchPage) {
            queryParams.country = searchParams.country.toString();
        }
        if (searchParams.language && isSearchPage) {
            queryParams.language = searchParams.language.toString();
        }
        if (searchParams.network && isSearchPage) {
            queryParams.network = searchParams.network.toString();
        }
        if (searchParams.director && isSearchPage) {
            queryParams.director = searchParams.director;
        }
        if (searchParams.actor && isSearchPage) {
            queryParams.actor = searchParams.actor;
        }

        var parts: string[] = [];
        var key: string;
        for (key in queryParams) {
            if (Object.prototype.hasOwnProperty.call(queryParams, key)) {
                parts.push(encodeURIComponent(key) + "=" + encodeURIComponent(queryParams[key]));
            }
        }

        if (window.history && window.history.replaceState) {
            var nextSearch = parts.length > 0 ? "?" + parts.join("&") : "";
            window.history.replaceState(null, "", window.location.pathname + nextSearch);
        }
    };

    if (isLoading) {
        return (
            <div className="movies-grid__loading">
                <img src={LoadingGif} alt="در حال بارگذاری" />
                <p className="movies-grid__loading-text">در حال بارگذاری...</p>
            </div>
        );
    }

    return (
        <div role="main" className="movies-grid">
            <img src={SectionTopBg} alt="" className="movies-grid__bg" />
            <div className="movies-grid__inner">
                <h1 className="movies-grid__title">{title}</h1>

                <div className="movies-grid__filters">
                    <CommonFilter
                        allowType={allowType}
                        allowGenre={allowGenre}
                        allowSort={allowSort}
                        allowImdbRating={allowImdbRating}
                        allowDubbed={allowDubbed}
                        allowSubtitled={allowSubtitled}
                        typeDefaultValue={searchParams.type ? searchParams.type : ""}
                        genreDefaultValue={searchParams.genre ? searchParams.genre.toString() : ""}
                        sortDefaultValue={searchParams.sort ? searchParams.sort : ""}
                        imdbRatingDefaultValue={searchParams.imdb_rate ? searchParams.imdb_rate.toString() : "0"}
                        dubbedDefaultValue={!!searchParams.is_dubbed}
                        subtitledDefaultValue={!!searchParams.is_subbed}
                        onTypeChange={function (value) { onFiltersChange("type", value); }}
                        onGenreChange={function (value) { onFiltersChange("genre", value); }}
                        onSortChange={function (value) { onFiltersChange("sort", value); }}
                        onImdbRatingChange={function (value) { onFiltersChange("imdb_rate", value); }}
                        onDubbedChange={function (value) { onFiltersChange("is_dubbed", value); }}
                        onSubtitledChange={function (value) { onFiltersChange("is_subbed", value); }}
                    />
                </div>

                <div className="movies-grid__cards">
                    {searchResult && searchResult.agent && (
                        <AgentCard agent={searchResult.agent} />
                    )}
                    {searchResult && searchResult.movies.map(function (movie) {
                        return (
                            <MovieCard
                                key={movie.id}
                                id={movie.id}
                                slug={movie.slug}
                                type={movie.type}
                                title={movie.title}
                                updateText={movie.update_text}
                                year={movie.year != null ? String(movie.year) : ""}
                                ratingValue={movie.imdb_rate != null ? String(movie.imdb_rate) : "0"}
                                thumbnail={movie.thumbnail}
                                isDubbed={movie.is_dubbed}
                                isSubtitled={movie.is_subbed}
                                genres={movie.genres ? movie.genres : []}
                            />
                        );
                    })}
                </div>

                {searchResult && searchResult.movies.length > 0 && (
                    <div className="movies-grid__pagination">
                        <PaginationView pagination={pagination} onChange={onPageChange} />
                    </div>
                )}
            </div>
        </div>
    );
}

function AgentCard({ agent }: { agent: SearchResultAgent }) {
    var posterSrc = agent.image.indexOf("http") === 0
        ? agent.image
        : ASSETS_BASE_URL + agent.image.replace("files/", "");
    var agentPath = "/" + agent.type + "s/" + agent.slug + "/";
    var roleLabel = agent.type === "actor" ? "بازیگر" : "کارگردان";

    return (
        <Link to={agentPath} target="_blank" className="movies-grid-agent">
            <div className="movies-grid-agent__poster">
                {agent.image.length > 0 && (
                    <img src={posterSrc} alt="" className="movies-grid-agent__bg-image" />
                )}
                {agent.image.length > 0 ? (
                    <img src={posterSrc} alt={agent.name} className="movies-grid-agent__avatar" />
                ) : (
                    <img src={NoPersonIcon} alt={agent.name} className="movies-grid-agent__avatar" />
                )}
            </div>
            <p className="movies-grid-agent__name">{agent.name}</p>
            <p className="movies-grid-agent__role">{roleLabel}</p>
        </Link>
    );
}
